import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { createLowlight, common } from 'lowlight';
import { Markdown } from 'tiptap-markdown';
import Callout from './Callout.js';
import { EmbedV2 } from './EmbedResource/EmbedExtension.js';
import NodeInfo from './NodeInfo.js';
import { TranscriptExtension } from './TranscriptExtension.js';
import { TranscriptItemExtension } from './TranscriptItemExtension.js';
import Embed from './Embed.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import ListKeymap from '@tiptap/extension-list-keymap';
import { BlockquoteExtension } from './BlockquoteExtension.js';
import { SearchHandler } from './SearchHandler.js';
import { DiffHandler } from './Diffs/DiffHandler.js';
import FbcNumberList from './Lists/FbcNumberList.js';
import FbcBulletList from './Lists/FbcBulletList.js';
import ListConverter from './Lists/ListConverter.js';
import FbcList from './Lists/FbcList.js';
import { ParagraphBlock } from './ParagraphBlock.js';
import { FbcLogic } from './FbcLogic.js';
import FbcCheckList from './Lists/FbcCheckList.js';
import FbcToggleList from './Lists/FbcToggleList.js';
import { NodeControl } from './NodeControl/NodeControlExtension.js';
import { YJSMappingExtension } from './YJSMapping/YJSMappingPlugin.js';
import { EmptyImage } from './EmptyNodes/EmptyImage.js';
import { EmptyFile } from './EmptyNodes/EmptyFile.js';
import { FbcDocument } from './FbcDocument.js';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import { defaultSelectionBuilder } from 'y-prosemirror';
import { ContextualMenuExtension } from './ContextualMenu/ContextualMenuExtension.js';
import { VirtualCursorExtension } from './VirtualCursor/VirtualCursor.js';
import { PlaceholderDocNode } from './PlaceholderDoc.js';

const getTiptapExtensions = (options = {}) => {
  const { yDoc, provider, userAwareness, contextualMenu, title, onTitleChange, virtualCursorTarget, suppressMissingUploadError } = options;
  return [
    ...yDoc ? [
      Collaboration.configure({
        document: yDoc
      })
    ] : [],
    ...provider ? [
      CollaborationCursor.configure({
        provider,
        user: userAwareness,
        render: (user) => {
          const cursor = document.createElement("span");
          cursor.classList.add("collaboration-cursor");
          if (user.color) cursor.style.setProperty("--yjs-cursor-color", user.color);
          if (user.name) cursor.dataset.yjsCursorName = user.name;
          return cursor;
        },
        selectionRender: (user) => {
          const defaultSelection = defaultSelectionBuilder(user);
          return {
            ...defaultSelection,
            style: `${defaultSelection.style}; --yjs-cursor-color: ${user.color}bb;`
          };
        }
      })
    ] : [],
    NodeControl,
    StarterKit.configure({
      codeBlock: false,
      history: false,
      dropcursor: {
        color: cssVar["color-app-primary-text"],
        width: 2
      },
      blockquote: false,
      orderedList: false,
      bulletList: false,
      listItem: false,
      document: false
    }),
    FbcDocument.configure({
      title,
      onTitleChange
    }),
    BlockquoteExtension,
    Placeholder.configure({
      placeholder: "Type something here…",
      emptyEditorClass: "is_empty",
      includeChildren: true
    }),
    Highlight.configure(),
    Underline.configure(),
    Link.configure(),
    CodeBlockLowlight.configure({
      lowlight: createLowlight(common),
      defaultLanguage: "js"
    }),
    Table.configure({
      resizable: true
    }),
    TableRow,
    TableHeader,
    TableCell,
    Callout,
    NodeInfo.configure({
      types: [
        "heading",
        "paragraph",
        "blockquote",
        "codeBlock",
        "embed",
        "listItem",
        "table",
        "tableRow",
        "tableCell",
        "callout",
        "link",
        "orderedList",
        "bulletList",
        "taskList",
        "listItem",
        "numberedList",
        "bulletedList",
        "embedV2",
        "transcript",
        "transcriptItem",
        "paragraphBlock",
        "fbcBulletList",
        "fbcNumberList",
        "fbcCheckList",
        "fbcToggleList",
        "emptyImage"
      ]
    }),
    Embed,
    EmbedV2.configure({
      suppressMissingUploadError
    }),
    Markdown.configure({
      html: true,
      bulletListMarker: "-",
      transformPastedText: true,
      transformCopiedText: false
    }),
    TranscriptExtension,
    TranscriptItemExtension,
    ListKeymap,
    SearchHandler,
    DiffHandler,
    FbcNumberList,
    FbcBulletList,
    FbcCheckList,
    FbcToggleList,
    FbcList,
    ListConverter,
    FbcLogic,
    ParagraphBlock,
    YJSMappingExtension,
    EmptyImage,
    EmptyFile,
    PlaceholderDocNode,
    ...contextualMenu ? [
      ContextualMenuExtension.configure(contextualMenu)
    ] : [],
    ...virtualCursorTarget ? [
      VirtualCursorExtension.configure({
        target: virtualCursorTarget
      })
    ] : []
  ];
};

export { getTiptapExtensions };
//# sourceMappingURL=index.js.map
